import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  NgZone,
  Output,
  ViewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { indexOfInsensitive } from '@wz-utilities/string.utilities';
import { AddressModels } from '../../../../../models/address.models';
const ROAD_TYPES = [
  'Oui',
  'Sans objet ou autre',
  'Allée',
  'Avenue',
  'Boulevard',
  'Carrefour',
  'Chemin',
  'Chaussée',
  'Cité',
  'Corniche',
  'Cours',
  'Domaine',
  'Descente',
  'Ecart',
  'Esplanade',
  'Faubourg',
  'Grande Rue',
  'Hameau',
  'Halle',
  'Impasse',
  'Lieu-dit',
  'Lotissement',
  'Marché',
  'Montée',
  'Passage',
  'Place',
  'Plaine',
  'Plateau',
  'Promenade',
  'Parvis',
  'Quartier',
  'Quai',
  'Résidence',
  'Ruelle',
  'Rocade',
  'Rond-point',
  'Route',
  'Rue',
  'Sente',
  'Sentier',
  'Square',
  'Terre plein',
  'Terre-plein',
  'Traverse',
  'Villa',
  'Village',
];

declare var google: any;

@Component({
  selector: 'wz-geo-autocomplete',
  templateUrl: './geo-autocomplete.component.html',
  styleUrls: ['./geo-autocomplete.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GeoAutoCompleteComponent),
      multi: true,
    },
  ],
})
export class GeoAutoCompleteComponent implements AfterViewInit {
  @ViewChild('autocomplete', { static: true }) el!: ElementRef;
  @Output() valueChange: EventEmitter<any> = new EventEmitter<any>();

  @Input() disabled!: boolean;
  @Input() placeholder: string = 'Rechercher une adresse ici';

  private _value!: AddressModels.Address;
  private autocomplete: any;
  private googlePlaceToTunnelSanteAddressMapping: {
    [property: string]: (value: string, address: AddressModels.Address) => void;
  } = {
    street_number: (value, address) => {
      const bisIndex = indexOfInsensitive(value, 'bis');
      if (bisIndex !== -1) {
        address.repetition = 'bis';
        address.streetNumber = +value.slice(0, bisIndex);
        return;
      }

      const terIndex = indexOfInsensitive(value, 'ter');
      if (terIndex !== -1) {
        address.repetition = 'ter';
        address.streetNumber = +value.slice(0, terIndex);
        return;
      }

      address.streetNumber = +value;
    },
    route: (value, address) => (address.streetName = value),
    locality: (value, address) => (address.cityName = value),
    postal_code: (value, address) => (address.cityZipCode = value),
  };

  constructor(private zone: NgZone) {}

  ngAfterViewInit() {
    this.autocomplete = new google.maps.places.Autocomplete(
      this.el.nativeElement,
      {
        types: ['address'],
        componentRestrictions: {
          country: 'fr',
        },
      },
    );
    google.maps.event.addListener(this.autocomplete, 'place_changed', () => {
      this.zone.run(() => {
        const place = this.autocomplete.getPlace();
        this.value = this.googlePlaceToTunnelSanteAddress(place, ROAD_TYPES);
      });
    });
  }

  private googlePlaceToTunnelSanteAddress(
    place: any,
    roadTypes: string[],
  ): AddressModels.Address {
    const address: AddressModels.Address | any = {
      cityName: '',
      complement: '',
      cityZipCode: '',
      repetition: '',
      streetName: '',
      streetNumber: null,
      streetType: '',
    };

    for (let i = 0; i < place.address_components.length; i++) {
      let addressType = place.address_components[i].types[0];
      if (this.googlePlaceToTunnelSanteAddressMapping[addressType]) {
        this.googlePlaceToTunnelSanteAddressMapping[addressType](
          place.address_components[i]['long_name'],
          address,
        );
      }
    }

    if (address.streetName) {
      let addressRoadType = roadTypes.find(
        (t) => address.streetName.indexOf(t) === 0,
      );
      if (addressRoadType) {
        address.streetType = addressRoadType;
        address.streetName = address.streetName
          .slice(addressRoadType.length)
          .trim();
      }
    }

    return address;
  }

  get value(): AddressModels.Address {
    return this._value;
  }

  set value(val: AddressModels.Address) {
    this._value = val;
    this.propagateChange(this.value);
    this.valueChange.next(this.value);
  }

  /**
   * IMPLEMENTATION CONTROLVALUEACCESSOR
   */
  writeValue(value: any) {
    if (value !== undefined && value !== null) {
      this.value = value;
    }
  }

  propagateChange = (_: any) => {};

  registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  registerOnTouched() {}
}
